import { createTheme, type PaletteMode, type Theme } from '@mui/material'
import { esES } from '@mui/material/locale'

/**
 * Paleta tomada del logo de Granja Santa María.
 *
 * El verde y el naranja son los colores de marca; el oliva viene de las
 * espigas y el crema del papel del sello.
 */
export const brand = {
  green: '#1E4D2B',
  greenLight: '#2F6B3D',
  orange: '#E87424',
  olive: '#A9B94C',
  cream: '#F6F3EA',
  ink: '#1A1A1A',
}

/**
 * Colores de estado de jornada.
 *
 * Deliberadamente distintos del verde de marca: si "presente" usara el mismo
 * verde que la navegación y los botones, un chip de estado se confundiría con
 * un elemento accionable. El naranja sí se comparte, porque en ambos casos
 * significa lo mismo: atención.
 */
const status = {
  present: '#2E7D32',
  late: brand.orange,
  absent: '#C62828',
  info: '#1565C0',
}

export function buildTheme(mode: PaletteMode): Theme {
  const isDark = mode === 'dark'

  return createTheme(
    {
      palette: {
        mode,
        primary: { main: isDark ? brand.greenLight : brand.green },
        secondary: { main: brand.orange },
        success: { main: status.present },
        warning: { main: status.late },
        error: { main: status.absent },
        info: { main: status.info },
        background: isDark
          ? { default: '#12160F', paper: '#1A1F16' }
          // Crema muy diluido: evoca el papel del sello sin volver ilegible
          // una pantalla que se mira ocho horas por día.
          : { default: brand.cream, paper: '#FFFFFF' },
        text: isDark ? undefined : { primary: brand.ink },
      },
      shape: { borderRadius: 12 },
      typography: {
        fontFamily: ['Inter', 'Segoe UI', 'Roboto', 'system-ui', 'sans-serif'].join(','),
        h1: { fontSize: '1.75rem', fontWeight: 600 },
        h2: { fontSize: '1.4rem', fontWeight: 600 },
        h3: { fontSize: '1.15rem', fontWeight: 600 },
        button: { textTransform: 'none', fontWeight: 600 },
      },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            // Al imprimir el QR de una sucursal solo debe salir el cartel:
            // se oculta todo y se reimprime únicamente #qr-imprimible.
            '@media print': {
              'body *': { visibility: 'hidden' },
              '#qr-imprimible, #qr-imprimible *': { visibility: 'visible' },
              '#qr-imprimible': {
                position: 'absolute',
                top: 0,
                left: '50%',
                transform: 'translateX(-50%)',
                border: 'none',
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: { backgroundImage: 'none' },
          },
        },
        MuiButton: {
          defaultProps: { disableElevation: true },
        },
        MuiTextField: {
          defaultProps: { size: 'small' },
        },
        MuiListItemButton: {
          styleOverrides: {
            root: ({ theme }) => ({
              '&.Mui-selected': {
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
                '& .MuiListItemIcon-root': { color: theme.palette.primary.contrastText },
                '&:hover': { backgroundColor: theme.palette.primary.dark },
              },
            }),
          },
        },
      },
    },
    // Textos internos de MUI en español (paginación, tablas, selects).
    esES,
  )
}

/** Color semántico de cada estado de jornada, para chips y tablas. */
export const dayStatusColor: Record<
  string,
  'success' | 'warning' | 'error' | 'info' | 'default'
> = {
  present: 'success',
  late: 'warning',
  absent: 'error',
  leave: 'info',
  vacation: 'info',
  incomplete: 'default',
}

export const dayStatusLabel: Record<string, string> = {
  present: 'Presente',
  late: 'Tarde',
  absent: 'Ausente',
  leave: 'Licencia',
  vacation: 'Vacaciones',
  incomplete: 'Incompleta',
}
